import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p2201Api, F2110, F2111 } from '../../../services/p2201Api';
import { Mode } from './useP2201Mode';
import { calcDetailAmount, calcTotals } from '../helpers';

export function useP2201Record() {
  const [record, setRecord] = useState<F2110 | null>(null);
  const [formData, setFormData] = useState<Partial<F2110>>({});
  const [details, setDetails] = useState<Partial<F2111>[]>([]);
  const [selectedDetailRow, setSelectedDetailRow] = useState(0);
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{ data: F2110 } | null> => {
    setLoading(true);
    try {
      const data = await p2201Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
      setSelectedDetailRow(0);
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Load a copy-as-new edit template. This call does not write the DB. */
  const loadCopyTemplate = useCallback(async (id: string): Promise<F2110 | null> => {
    setLoading(true);
    try {
      const data = await p2201Api.copyTemplate(id);
      setRecord(null);
      setFormData({ ...data });
      setDetails(data.details || []);
      setSelectedDetailRow(0);
      return data;
    } catch {
      message.error('無法建立複製草稿');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof F2110, value: F2110[keyof F2110]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof F2110): F2110[keyof F2110] => {
    return (formData as Record<string, F2110[keyof F2110]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) [T-F01: use mutation response] */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F2110) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as F2110,
        details: details as F2111[],
      };
      if (mode === 'add') {
        const result = await p2201Api.create(req);
        message.success(`新增成功: ${result.f2110_01}`);
        onSuccess(result);
      } else {
        const result = await p2201Api.update(formData.f2110_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      // [T-F06: join 422 errors for display]
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await p2201Api.delete(record.f2110_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a detail row (max 99) */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.f2111_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('明細最多 99 項');
      return;
    }
    setDetails(prev => [...prev, {
      f2111_01: formData.f2110_01 || '',
      f2111_02: maxSeq + 1,
      f2111_07: (formData.f2110_07 as string) || '',
      f2111_11: '',
      f2111_11a: '',
      f2111_12: '',
      f2111_13: 0,
      f2111_14: 0,
      f2111_15: 0,
      f2111_18: '',
    }]);
  }, [details, formData.f2110_01, formData.f2110_07]);

  /** Delete the selected detail row [T-F05: reindex] */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedDetailRow);
      return next.map((d, i) => ({ ...d, f2111_02: i + 1 }));
    });
    setSelectedDetailRow(Math.max(0, selectedDetailRow - 1));
  }, [details, selectedDetailRow]);

  /** Update a single detail field with auto-calc (BR-07/08) */
  const setDetailField = useCallback((rowIdx: number, key: keyof F2111, value: F2111[keyof F2111]) => {
    setDetails(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx], [key]: value };
      // Auto-calculate amount when qty or price changes
      if (key === 'f2111_13' || key === 'f2111_14') {
        const qty = key === 'f2111_13' ? (value as number) : (row.f2111_13 || 0);
        const price = key === 'f2111_14' ? (value as number) : (row.f2111_14 || 0);
        row.f2111_15 = calcDetailAmount(qty, price);
      }
      next[rowIdx] = row;
      return next;
    });
  }, []);

  /** Recalculate master totals from current details + formData (BR-24/25) */
  const recalcMasterTotals = useCallback(() => {
    const detailSum = details.reduce((sum, d) => sum + (d.f2111_15 || 0), 0);
    const transportation = (formData.f2110_23 as number) || 0;
    const freight = (formData.f2110_25 as number) || 0;
    const taxType = (formData.f2110_18 as number) || 2;
    const { subtotal, tax, grandTotal } = calcTotals(detailSum, transportation, freight, taxType);
    setFormData(prev => ({
      ...prev,
      f2110_17: detailSum,
      f2110_28: subtotal,
      f2110_19: tax,
      f2110_20: grandTotal,
    }));
  }, [details, formData.f2110_23, formData.f2110_25, formData.f2110_18]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for a new add (BR-18 defaults) */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({
      f2110_80: 'N',
      f2110_18: 2,  // default tax-free
      f2110_17: 0,
      f2110_19: 0,
      f2110_20: 0,
      f2110_21: 0,
      f2110_23: 0,
      f2110_25: 0,
      f2110_28: 0,
      f2110_31: '',
    });
    setDetails([]);
    setSelectedDetailRow(0);
  }, []);

  /** Apply result after save [T-F01] */
  const applyResult = useCallback((result: F2110) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details,
    selectedDetailRow, loading,
    setSelectedDetailRow,
    setDetails,
    loadRecord, loadCopyTemplate, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    setDetailField, recalcMasterTotals,
    revertToRecord, resetForAdd, applyResult,
  };
}
